import React, { useState, useEffect } from 'react';
import { FormControl, Select, MenuItem, Typography } from '@mui/material';
import { organizationApi } from '../../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import { ENGAGEMENT_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

/**
 * EngagementTypeSelector component for selecting employee engagement types
 * 
 * @param {Object} props - Component props
 * @param {string|null} props.value - Currently selected engagement type ID or name
 * @param {Function} props.onChange - Callback function called when selection changes
 * @param {string} props.returnValue - What to return when selection changes (ID or NAME)
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.disabled - Whether the selector is disabled
 * @param {string} props.placeholder - Placeholder text for empty selection
 * @param {Object} props.sx - Additional styling
 * @param {boolean} props.hideLabel - Whether to hide the label (useful for DataGrid)
 * @param {Function} props.onOpen - Callback function called when the selector is opened
 * @param {Function} props.onClose - Callback function called when the selector is closed
 * @param {Array} props.disabledEngagementTypeCodes - Array of engagement type codes to disable
 * @param {Object} props.formControlProps - Additional props for the FormControl component
 * @returns {JSX.Element} EngagementTypeSelector component
 */
const EngagementTypeSelector = ({
  value = null,
  onChange,
  returnValue = ENGAGEMENT_TYPE_RETURN_TYPES.ID,
  required = false,
  disabled = false,
  placeholder = "Select an engagement type",
  sx = {},
  hideLabel = false,
  onOpen,
  onClose,
  disabledEngagementTypeCodes = [],
  ...formControlProps
}) => {
  const [engagementTypes, setEngagementTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEngagementTypes();
  }, []);

  const fetchEngagementTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await organizationApi.getEmployeeEngagementTypes();
      setEngagementTypes(data || []);
    } catch (err) {
      console.error('Error fetching engagement types:', err);
      setError('Failed to load engagement types');
      setEngagementTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    
    if (!selectedValue) {
      onChange?.(null);
      return;
    }

    const selectedEngagementType = engagementTypes.find(type => type.id === selectedValue);
    
    if (!selectedEngagementType) {
      onChange?.(null);
      return;
    }

    const returnData = returnValue === ENGAGEMENT_TYPE_RETURN_TYPES.NAME 
      ? selectedEngagementType.name 
      : selectedEngagementType.id;
    
    onChange?.(returnData);
  };

  // Convert value to ID for consistent internal handling
  const getSelectedId = () => {
    if (!value) return '';
    
    if (returnValue === ENGAGEMENT_TYPE_RETURN_TYPES.NAME) {
      const engagementType = engagementTypes.find(type => type.name === value);
      return engagementType?.id || '';
    }
    
    return value;
  };

  if (loading) {
    return (
      <FormControl fullWidth sx={sx} {...formControlProps}>
        {!hideLabel && (
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Loading engagement types...
          </Typography>
        )}
        <LoadingSpinner size={24} />
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth sx={sx} {...formControlProps}>
        {!hideLabel && (
          <Typography variant="body2" sx={{ mb: 1, color: 'error.main' }}>
            {error}
          </Typography>
        )}
        <Select
          value=""
          disabled
          displayEmpty
        >
          <MenuItem value="">
            <em>Error loading engagement types</em>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth sx={sx} {...formControlProps}>
      <Select
        value={getSelectedId()}
        onChange={handleChange}
        displayEmpty
        required={required}
        disabled={disabled}
        onOpen={onOpen}
        onClose={onClose}
      >
        <MenuItem value="">
          <em>{placeholder}</em>
        </MenuItem>
        {engagementTypes.map((type) => {
          const isDisabled = disabledEngagementTypeCodes.includes(type.code);
          return (
            <MenuItem 
              key={type.id} 
              value={type.id}
              disabled={isDisabled}
              sx={isDisabled ? { 
                color: 'text.disabled',
                opacity: 0.6 
              } : {}}
            >
              {type.name}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default EngagementTypeSelector; 